import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { authenticationService } from "@authentication/services/AuthenticationService";

const ResetPassword = () => {
  const { token } = useParams();

  const [formData, setFormData] = useState({
    password: "",
    passwordConfirm: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetSuccessful, setResetSuccessful] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  useEffect(() => {
    if (!token) {
      setMessage("Invalid or missing reset token.");
      setMessageType("error");
    }
  }, [token]);

  const validatePassword = (password) => {
    if (!password || password.trim() === "") return "Password is required";
    if (password.length < 8)
      return "Password must be at least 8 characters long";
    if (!/(?=.*[a-z])/.test(password))
      return "Password must contain at least one lowercase letter";
    if (!/(?=.*[A-Z])/.test(password))
      return "Password must contain at least one uppercase letter";
    if (!/(?=.*\d)/.test(password))
      return "Password must contain at least one number";
    if (!/(?=.*[@$!%*?&])/.test(password))
      return "Password must contain at least one special character (@$!%*?&)";
    return null;
  };

  const EyeIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      color="#225745"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  const EyeOffIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      color="#f4b952"
    >
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const togglePasswordConfirmVisibility = () => {
    setShowPasswordConfirm(!showPasswordConfirm);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setValidationErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
    setMessage("");
    setMessageType("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic token check
    if (!token) {
      setMessage("Invalid reset token. Please request a new password reset.");
      setMessageType("error");
      return;
    }

    const errors = {};
    const passwordError = validatePassword(formData.password);
    if (passwordError) errors.password = passwordError;

    if (!formData.passwordConfirm.trim()) {
      errors.passwordConfirm = "Please confirm your new password";
    } else if (formData.password !== formData.passwordConfirm) {
      errors.passwordConfirm = "Passwords do not match";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setMessage("Please correct the highlighted errors.");
      setMessageType("error");
      return;
    }

    setIsSubmitting(true);
    try {
      await authenticationService.resetPassword(
        token,
        formData.password,
        formData.passwordConfirm
      );
      setMessage(
        "Password reset successfully! Closing this page shortly. You may now login to your account using the new password."
      );
      setMessageType("success");
      setResetSuccessful(true);

      setTimeout(() => {
        window.close();
      }, 5000);
    } catch (error) {
      let errorMessage = "Failed to reset password. Please try again.";

      // Check if error response exists before accessing properties
      if (error && error.response) {
        if (error.response.status === 400) {
          errorMessage =
            "Invalid or expired reset token. Please request a new password reset.";
        } else if (error.response.status === 429) {
          errorMessage = "Too many reset attempts. Please try again later.";
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }

      setMessage(errorMessage);
      setMessageType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show form if token exists and reset not successful
  const showForm = token && !resetSuccessful;

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logos Section */}
        <div style={styles.logosContainer}>
          <img
            src="/images/university-logo.png"
            alt="University Logo"
            style={styles.logo}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <img
            src="/images/lrc-logo.png"
            alt="LRC Logo"
            style={styles.logo}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>

        <div style={styles.header}>
          <h1 style={styles.title}>Reset Your Password</h1>
          <p style={styles.subtitle}>
            Please enter your new password below to complete the reset process.
          </p>
        </div>

        {/* Message Display */}
        {message && (
          <div
            style={{
              ...styles.message,
              ...(messageType === "success"
                ? styles.messageSuccess
                : styles.messageError),
            }}
            role="alert"
            aria-live="polite"
          >
            {message}
          </div>
        )}

        {/* Show form only if token exists and reset not successful */}
        {showForm && (
          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>New Password</label>
              <div style={styles.inputContainer}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...styles.inputWithIcon,
                    ...(validationErrors.password ? styles.inputError : {}),
                  }}
                  disabled={isSubmitting}
                  required
                  autoFocus
                  placeholder="Enter your new password"
                  aria-label="New Password"
                  aria-describedby={
                    validationErrors.password ? "password-error" : undefined
                  }
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  style={styles.eyeButton}
                  className="eye-button"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
              {validationErrors.password && (
                <div style={styles.validationError} id="password-error">
                  {validationErrors.password}
                </div>
              )}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Confirm New Password</label>
              <div style={styles.inputContainer}>
                <input
                  type={showPasswordConfirm ? "text" : "password"}
                  name="passwordConfirm"
                  value={formData.passwordConfirm}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...styles.inputWithIcon,
                    ...(validationErrors.passwordConfirm
                      ? styles.inputError
                      : {}),
                  }}
                  disabled={isSubmitting}
                  required
                  placeholder="Confirm your new password"
                  aria-label="Confirm New Password"
                  aria-describedby={
                    validationErrors.passwordConfirm
                      ? "confirm-error"
                      : undefined
                  }
                />
                <button
                  type="button"
                  onClick={togglePasswordConfirmVisibility}
                  style={styles.eyeButton}
                  className="eye-button"
                  tabIndex={-1}
                  aria-label={
                    showPasswordConfirm ? "Hide password" : "Show password"
                  }
                >
                  {showPasswordConfirm ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </div>
              {validationErrors.passwordConfirm && (
                <div style={styles.validationError} id="confirm-error">
                  {validationErrors.passwordConfirm}
                </div>
              )}
            </div>

            <button
              type="submit"
              style={{
                ...styles.button,
                ...(isSubmitting ? styles.buttonDisabled : {}),
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span style={styles.loadingText}>
                  <span style={styles.spinner} />
                  Resetting...
                </span>
              ) : (
                "Reset Password"
              )}
            </button>
          </form>
        )}

        {/* Show message when no token */}
        {!token && !resetSuccessful && (
          <div style={styles.tokenExpiredContainer}>
            <div style={styles.warningIcon}>⚠️</div>
            <div style={styles.expiredMessage}>
              Invalid password reset link.
            </div>
            <a href="/forgot-password" style={styles.requestNewLink}>
              Request a new password reset link
            </a>
          </div>
        )}

        {resetSuccessful && (
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>✓</div>
            <div style={styles.closeNote}>
              If this page does not close automatically, you may now close this
              tab.
            </div>
          </div>
        )}

        <div style={styles.footer}>
          <p style={styles.footerText}>Remember your password?</p>
          <a href="/login" style={styles.link}>
            Back to Sign In
          </a>
          <span style={styles.footerText}> or </span>
          <a href="/signup" style={styles.link}>
            Create Account
          </a>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    background: "#225745",
    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
  },
  card: {
    background: "rgba(255, 255, 255, 0.95)",
    borderRadius: "16px",
    boxShadow: "0 20px 40px rgba(133, 134, 40, 0.3)",
    padding: "40px",
    width: "100%",
    maxWidth: "450px",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
  },
  logosContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "20px",
    marginBottom: "30px",
    paddingBottom: "20px",
    borderBottom: "1px solid #eee",
  },
  logo: {
    maxHeight: "100px",
    maxWidth: "120px",
    objectFit: "contain",
    filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))",
  },
  header: {
    textAlign: "center",
    marginBottom: "30px",
  },
  title: {
    color: "#225745",
    fontSize: "2rem",
    fontWeight: "600",
    marginBottom: "8px",
    letterSpacing: "-0.5px",
  },
  subtitle: {
    color: "#666",
    fontSize: "0.95rem",
    lineHeight: "1.5",
    marginBottom: "0",
  },
  formGroup: {
    marginBottom: "20px",
  },
  inputContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  label: {
    display: "block",
    marginBottom: "8px",
    color: "#333",
    fontSize: "0.9rem",
    fontWeight: "500",
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    border: "2px solid #e1e5e9",
    borderRadius: "8px",
    fontSize: "1rem",
    transition: "all 0.3s ease",
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
  },
  inputWithIcon: {
    paddingRight: "45px",
  },
  eyeButton: {
    position: "absolute",
    right: "12px",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "1.2rem",
    color: "#666",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "color 0.3s ease",
    userSelect: "none",
    outline: "none",
  },
  inputError: {
    borderColor: "#dc3545",
    boxShadow: "0 0 0 3px rgba(220, 53, 69, 0.1)",
  },
  validationError: {
    color: "#dc3545",
    fontSize: "0.85rem",
    marginTop: "5px",
    lineHeight: "1.4",
  },
  button: {
    width: "100%",
    padding: "14px",
    background: "#f4b952",
    color: "#000",
    border: "none",
    borderRadius: "8px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    position: "relative",
  },
  buttonDisabled: {
    opacity: "0.7",
    cursor: "not-allowed",
    background: "#ccc",
  },
  loadingText: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  spinner: {
    width: "16px",
    height: "16px",
    border: "2px solid transparent",
    borderTop: "2px solid #000",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  footer: {
    textAlign: "center",
    marginTop: "30px",
    paddingTop: "20px",
    borderTop: "1px solid #eee",
  },
  footerText: {
    color: "#666",
    margin: "0 5px",
    fontSize: "0.9rem",
  },
  link: {
    color: "#225745",
    textDecoration: "none",
    fontWeight: "500",
    transition: "color 0.3s ease",
  },
  message: {
    padding: "12px 16px",
    marginBottom: "20px",
    borderRadius: "8px",
    background: "#f8f9fa",
    borderLeft: "4px solid #225745",
    color: "#333",
    fontSize: "0.9rem",
    lineHeight: "1.4",
  },
  messageSuccess: {
    background: "#d4edda",
    borderLeftColor: "#28a745",
    color: "#155724",
  },
  messageError: {
    background: "#f8d7da",
    borderLeftColor: "#dc3545",
    color: "#721c24",
  },
  successContainer: {
    textAlign: "center",
    padding: "20px 0",
  },
  successIcon: {
    fontSize: "3rem",
    color: "#28a745",
    marginBottom: "15px",
    fontWeight: "bold",
  },
  closeNote: {
    fontSize: "0.9rem",
    color: "#333",
    lineHeight: "1.5",
  },

  tokenExpiredContainer: {
    textAlign: "center",
    padding: "20px 0",
  },
  warningIcon: {
    fontSize: "3rem",
    marginBottom: "15px",
  },
  expiredMessage: {
    fontSize: "1rem",
    color: "#333",
    marginBottom: "20px",
    lineHeight: "1.5",
  },
  requestNewLink: {
    display: "inline-block",
    padding: "12px 24px",
    background: "#225745",
    color: "#fff",
    textDecoration: "none",
    borderRadius: "8px",
    fontWeight: "500",
    transition: "background-color 0.3s ease",
  },
};

// Add CSS for spinner animation
const spinnerStyle = document.createElement("style");
spinnerStyle.innerHTML = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .eye-button {
    border: none !important;
    background: none !important;
    outline: none !important;
  }
  
  .eye-button:hover {
    color: #333 !important;
    transform: scale(1.1);
  }
  
  .eye-button:active {
    transform: scale(0.95);
  }
`;
document.head.appendChild(spinnerStyle);

export default ResetPassword;
